import APIs from '../../../api'
import { apiClient } from '../../../api/client'

// GET /external-modules/{uuid} — full external-module record used by the
// Duplicate flow. The list payload is paginated and doesn't carry the
// nested sensor/manufacturer/attachWithDevice objects, so we hit this
// endpoint to seed the registration modal.
const getExternalModuleByUuidApi = (uuid) => apiClient.get(APIs.EXTERNAL_MODULES.byUuid(uuid))

// GET /external-modules/unattached — external modules not currently
// coupled to any V-Tag. Backed by the same backend DTO contract as
// `listExternalModulesApi` (page/limit + optional filters), so callers
// (the couple/swap modal) can reuse the same param shape they already
// use for /devices/unattached. `orderBy` and `order` are forwarded
// verbatim so sortable server-side ordering (`createdAt`, …) works the
// same as the devices/v-tags lists. Used by the couple/swap modal so the
// user can only pick a free external module for the new coupling when
// the V-Tag's `type` is EXTERNAL_MODULE.
const listUnattachedExternalModules = (params = {}) => {
  const { per_page, current_page, page, limit, search, orderBy, order } = params || {}
  const query = { page: current_page ?? page, limit: per_page ?? limit }
  if (search) query.search = search
  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order
  return apiClient.get(APIs.EXTERNAL_MODULES.unattached, { params: query })
}

// GET /external-modules — list external modules. Mirrors `listDevicesApi`'s
// positional `filterFields` / `filterInputs` convention so PaginationFooter
// can forward `sensorId` and `manufacturerId` (and any future filters) by
// adding them to the table's filter array. `search`, `orderBy`, and `order`
// are forwarded verbatim so server-side filtering/sorting works the same as
// the devices/v-tags lists.
const listExternalModulesApi = ({
  per_page,
  current_page,
  search,
  orderBy,
  order,
  filterFields,
  filterInputs,
} = {}) => {
  const query = { page: current_page, limit: per_page }

  if (search) query.search = search

  filterFields?.forEach((field, index) => {
    const value = filterInputs?.[index]
    if (value !== '' && value != null) query[field] = value
  })

  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order

  return apiClient.get(APIs.EXTERNAL_MODULES.list, { params: query })
}

// External module registration accepts the same multipart shape as device
// registration. The backend uploads `calibrationCertificate` to S3 and stores
// the resulting calibration record ID with the external module.
const createExternalModuleApi = (payload = {}, calibrationFile = null) => {
  const body = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null || value === '') return
    body.append(key, Array.isArray(value) ? value.join(',') : String(value))
  })

  if (calibrationFile) body.append('calibrationCertificate', calibrationFile)

  return apiClient.post(APIs.EXTERNAL_MODULES.create, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// PUT /external-modules/{uuid} — external-module update. Mirrors the
// create endpoint's multipart body shape so the backend accepts the same
// field set on the same route. Triggered from the row-level Edit action;
// `calibrationFile` is the optional new calibration certificate — the
// existing file is preserved server-side when no file is appended.
const updateExternalModuleApi = (uuid, payload = {}, calibrationFile = null) => {
  const body = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null || value === '') return
    body.append(key, Array.isArray(value) ? value.join(',') : String(value))
  })

  if (calibrationFile) body.append('calibrationCertificate', calibrationFile)

  return apiClient.put(APIs.EXTERNAL_MODULES.update(uuid), body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// GET /external-modules/export — streams a CSV of the filtered external-
// module set. The backend honours the same search/filter/sort params as
// `listExternalModulesApi` (moduleName, sensorId, manufacturerId, …)
// but ignores `page`/`limit` so the export contains every matching row.
// `columns` is an array; axios serializes it as repeated
// `columns=…&columns=…` query params which matches the documented curl.
// `responseType: 'blob'` keeps the CSV payload opaque so axios doesn't
// try to JSON-parse a `text/csv` body. Mirrors `exportAccessPointsCsv` /
// `exportDevicesCsv` so the Sensors page can wire every tab through the
// same `Export CSV` action menu.
const exportExternalModulesCsv = async ({ columns, orderBy, order, ...filters } = {}) => {
  const params = {}
  if (columns?.length) params.columns = columns
  if (orderBy) params.orderBy = orderBy
  if (order) params.order = order
  // Forward every remaining filter key as-is. Empty strings are dropped
  // server-side, so sending `''` for unset filters is harmless — but we
  // strip them here to keep the URL clean.
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v != null) params[k] = v
  })
  const response = await apiClient.get(APIs.EXTERNAL_MODULES.export, {
    params,
    responseType: 'blob',
  })
  return response.data
}

export {
  listExternalModulesApi,
  createExternalModuleApi,
  getExternalModuleByUuidApi,
  updateExternalModuleApi,
  listUnattachedExternalModules,
  exportExternalModulesCsv,
}
export default {
  listExternalModulesApi,
  createExternalModuleApi,
  getExternalModuleByUuidApi,
  updateExternalModuleApi,
  listUnattachedExternalModules,
  exportExternalModulesCsv,
}
